import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Printer, ArrowLeft, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "acorn_admin_token";

// Tiny markdown renderer — just enough for our plan format
function renderMarkdown(md) {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  let inOrdered = false;
  const closeLists = () => {
    if (inList) { out.push("</ul>"); inList = false; }
    if (inOrdered) { out.push("</ol>"); inOrdered = false; }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { closeLists(); continue; }
    if (line.startsWith("### ")) {
      closeLists();
      out.push(`<h3 class="font-display text-xl tracking-tight mt-5 mb-2">${line.slice(4)}</h3>`);
    } else if (line.startsWith("## ")) {
      closeLists();
      out.push(`<h2 class="font-display text-2xl tracking-tight mt-7 mb-3 text-safety border-b-2 border-forest pb-1">${line.slice(3)}</h2>`);
    } else if (line.startsWith("# ")) {
      closeLists();
      out.push(`<h1 class="font-display text-4xl tracking-tight mb-4">${line.slice(2)}</h1>`);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      if (!inList) { out.push("<ul class='list-disc pl-6 space-y-1 mb-3'>"); inList = true; }
      out.push(`<li>${formatInline(line.slice(2))}</li>`);
    } else if (/^\d+\.\s/.test(line)) {
      if (!inOrdered) { out.push("<ol class='list-decimal pl-6 space-y-1 mb-3'>"); inOrdered = true; }
      out.push(`<li>${formatInline(line.replace(/^\d+\.\s/, ""))}</li>`);
    } else {
      closeLists();
      out.push(`<p class='mb-3 leading-relaxed'>${formatInline(line)}</p>`);
    }
  }
  closeLists();
  return out.join("");
}

function formatInline(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

export default function JobPlan() {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setError("Sign in to your Brain first.");
      return;
    }
    axios.get(`${API}/brain/job-plan/${planId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setPlan(data))
      .catch(() => setError("Plan not found or session expired."));
  }, [planId]);

  if (error) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center p-6 font-body">
        <div className="card-brutal p-8 text-center max-w-md">
          <div className="font-display text-2xl mb-2">Whoops</div>
          <p className="text-forest/80 mb-4">{error}</p>
          <a href="/brain" className="btn-primary px-5 py-3 inline-block">← Back to Brain</a>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center font-body">
        <Loader2 className="w-8 h-8 animate-spin text-safety" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone font-body text-ink">
      {/* TOOLBAR — hidden when printing */}
      <div className="print:hidden bg-forest text-bone border-b-2 border-forest sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <a href="/brain" className="text-bone hover:text-safety flex items-center gap-2 text-sm uppercase tracking-widest font-bold" data-testid="back-to-brain">
            <ArrowLeft className="w-4 h-4" /> Back to Brain
          </a>
          <button
            onClick={() => window.print()}
            className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
            data-testid="print-plan-btn"
          >
            <Printer className="w-4 h-4" strokeWidth={2.5} /> PRINT / SAVE PDF
          </button>
        </div>
      </div>

      {/* PRINTABLE SHEET */}
      <div className="max-w-4xl mx-auto p-6 sm:p-10 bg-white border-2 border-forest my-6 hard-shadow-lg print:border-0 print:shadow-none print:my-0">
        {/* Letterhead */}
        <div className="flex items-center justify-between border-b-2 border-forest pb-4 mb-6">
          <div className="flex items-center gap-3">
            <img src="/acorn-logo.jpg" alt="Acorn Tree Service²" className="h-16 w-auto" />
            <div>
              <div className="font-display text-2xl tracking-wider">ACORN TREE SERVICE²</div>
              <div className="text-xs uppercase tracking-[0.25em] text-forest/70">Kingston, TN · (865) 621-0995</div>
              <div className="text-xs text-forest/60">acorntreeservice2865.com · Locally Owned · Insured</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-safety">JOB PLANNING SHEET</div>
            <div className="text-xs text-forest/60 mt-1">{new Date(plan.created_at).toLocaleDateString()}</div>
            <div className="text-[10px] text-forest/40 mt-1 font-mono">#{plan.id.slice(0, 8).toUpperCase()}</div>
          </div>
        </div>

        {/* CONTENT */}
        <div
          data-testid="plan-body"
          className="prose-acorn text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(plan.plan_markdown) }}
        />

        {/* SIGNATURE LINES */}
        <div className="mt-10 pt-6 border-t-2 border-forest grid grid-cols-2 gap-8">
          <div>
            <div className="border-b-2 border-forest pb-1 mb-1 h-10" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-forest/70">Contractor Signature · Date</div>
          </div>
          <div>
            <div className="border-b-2 border-forest pb-1 mb-1 h-10" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-forest/70">Property Owner Signature · Date</div>
          </div>
        </div>

        <div className="mt-8 text-[10px] uppercase tracking-[0.25em] text-forest/50 text-center">
          <FileText className="w-3 h-3 inline mr-1" /> Generated by Acorn² Brain · For planning &amp; permit submission prep only · All city inspections required
        </div>
      </div>

      {/* PRINT STYLES */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          @page { size: letter; margin: 0.5in; }
        }
      `}</style>
    </div>
  );
}
